import { useCallback, useEffect, useState } from "react";
import { DataResult, State } from "@progress/kendo-data-query";
import { GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import _ from "lodash";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import { GridDataLoaderCutOffNextTry } from "./GridDataLoaderCutOffNextTry";
import { InitialState } from "../../Grid/InitialDataState";
import InlineEditProps from "../../Grid/InlineEdit/InlineEditProps";
import { ManageCutOffGridColumns } from "./ManageCutOffGridColumns";
import { ManageCutOffControl } from "./ManageCutOffControl";
import { getBranchesRequest } from "./GetBranchesRequest";
import { BranchViewModel } from "./BranchViewModel";
import { ManageCutOffViewModel } from "./ManageCutOffViewModel";
import { saveChanges } from "./SaveChanges";
import StyledKendoGrid from "../../Grid/StyledKendoGrid";
import { cutOffTimeUrls } from "../../URL/ApplicationUrls";
import { combinedUrls } from "../../URL/functions";
import useSearchParams from "../../URL/useSearchParams";
import PageHeader from "../../Layout/Header/PageHeader";
import PageHeaderButton from "../../Layout/Header/PageHeaderButton";
import RenderAuthorized from "../../Auth/RenderAuthorized";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";

export function ManageCutOffGrid() {
  const { rateAgreementId } = useParams();
  const searchParams = useSearchParams();
  const rateAgreementName = searchParams.get("rateAgreementName");
  const carrierName = searchParams.get("carrierName");
  const url = combinedUrls(
    cutOffTimeUrls.forRateAgreement.apiUrlForId(rateAgreementId),
    "KendoGrid"
  );
  const idField = "OriginRegionID";

  const [dataState, setDataState] = useState<State>(InitialState);
  const initialDataResultState = { data: [], total: 0 };
  const [branches, setBranches] = useState<Array<BranchViewModel>>([]);

  const [dataResult, setDataResult] = useState<DataResult>(initialDataResultState);

  useEffect(() => {
    const internalGetBranches = async () => {
      const result = await getBranchesRequest(rateAgreementId);
      setBranches(result.branches);
    };

    internalGetBranches();
  }, [rateAgreementId]);

  function getBranchIds(): number[] {
    const branchIDsFromDataResult = dataResult.data.map((x) => {
      return x.BranchID;
    });
    return _.uniq(branchIDsFromDataResult);
  }

  function filterBranches(): Array<BranchViewModel> {
    const filteredBranches = branches.filter((b) => getBranchIds().includes(b.BranchID));
    const x: Array<BranchViewModel> = [];
    filteredBranches.forEach((b) => {
      x.push({ BranchID: b.BranchID, Name: b.Name });
    });
    return x;
  }

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const onDataReceived = useCallback((newDataResult: DataResult) => {
    setDataResult(newDataResult);
  }, []);

  const onDataResultChanged = (newDataResult: DataResult) => {
    setDataResult(newDataResult);
  };

  const [editState, setEditState] = useState<number>();
  const inlineEditProps = InlineEditProps({
    idField,
    dataResult,
    setEditState,
    onDataReceived: onDataResultChanged,
    isReadonly: false,
  });

  const theme = useTheme();
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  return (
    <>
      <PageHeader headerText="LTL Cut-Off Times">
        <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireAdminNetworkRoles}>
          <PageHeaderButton onClick={() => saveChanges(dataResult.data)}>Save</PageHeaderButton>
        </RenderAuthorized>
      </PageHeader>
      <div>
        <b>Rate Agreement:</b> {rateAgreementName}
      </div>
      <br />
      <div>
        <b>Carrier:</b> {carrierName}
      </div>
      <br />

      <div>
        <StyledKendoGrid
          muiTheme={theme}
          data={{
            data: dataResult.data
              .filterBy(dataState)
              .orderBy(dataState)
              .withEditfield(idField, editState),
            total: dataResult.total,
          }}
          dataItemKey={idField}
          sortable
          scrollable="scrollable"
          resizable
          {...dataState}
          onDataStateChange={onDataStateChange}
          {...inlineEditProps}
        >
          {ManageCutOffGridColumns({
            filteredBranches: filterBranches(),
            branches,
            isReadOnly: !isUserInAdminNetworkRole,
          })}
        </StyledKendoGrid>
        <br />
        <ManageCutOffControl
          data={
            dataResult.data
              .filterBy(dataState)
              .orderBy(dataState)
              .withEditfield(idField, editState) as ManageCutOffViewModel[]
          }
          onDataChanged={(changedData: ManageCutOffViewModel[]) => {
            // eslint-disable-next-line no-restricted-syntax
            for (const changedItem of changedData) {
              const match = dataResult.data.find(
                (x) => _.get(x, idField) === _.get(changedItem, idField)
              );
              match.CutOffTime = changedItem.CutOffTime;
              match.ReturnCutOffTime = changedItem.ReturnCutOffTime;
              match.TransitTime = changedItem.TransitTime;
              match.ReturnTransitTime = changedItem.ReturnTransitTime;
              match.BranchID = changedItem.BranchID;
            }
            setDataResult({
              data: dataResult.data,
              total: dataResult.data.length,
            });
          }}
          branches={branches}
          isReadOnly={!isUserInAdminNetworkRole}
        />
        <GridDataLoaderCutOffNextTry
          dataState={dataState}
          onDataReceived={onDataReceived}
          url={url}
          httpMethod="POST"
          rateAgreementId={rateAgreementId === null ? undefined : rateAgreementId}
        />
      </div>
    </>
  );
}
